import { useRef } from "react";
import { motion, useInView } from "framer-motion";

const BLIPS: [number, number][] = [
    [22, 30],
    [68, 58],
    [44, 76],
];

export const NetworkGraph = ({ className = "", animate = true }: { className?: string; animate?: boolean }) => (
    <div
        className={`pointer-events-none absolute ${className}`}
        aria-hidden
        data-testid="backdrop-network"
    >
        <div className="relative aspect-square w-full">
            {/* reticle rings */}
            {[100, 74, 50, 28].map((size, i) => (
                <motion.span
                    key={size}
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border"
                    style={{
                        width: `${size}%`,
                        height: `${size}%`,
                        borderColor: i === 2 ? "rgba(217, 168, 78, 0.3)" : "rgba(91, 147, 232, 0.22)",
                    }}
                    initial={animate ? { scale: 0.65, opacity: 0 } : false}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 1.3, delay: i * 0.16, ease: [0.22, 1, 0.36, 1] }}
                />
            ))}
            {/* crosshair axes */}
            <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-[#5B93E8]/15" />
            <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-[#5B93E8]/15" />
            {/* rotating radar sweep */}
            <span className={animate ? "radar-sweep" : "radar-sweep radar-sweep--static"} />
            {/* centre point */}
            <span className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#D9A84E]" />
            {/* trace blips */}
            {BLIPS.map(([x, y], i) => (
                <span key={i} className="absolute flex h-2 w-2" style={{ left: `${x}%`, top: `${y}%` }}>
                    <span
                        className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#5B93E8] opacity-50"
                        style={{ animationDelay: `${i * 0.9}s` }}
                    />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-[#5B93E8]/80" />
                </span>
            ))}
        </div>
    </div>
);

export const ContourLines = ({ className = "", rings = 14 }: { className?: string; rings?: number }) => {
    const ref = useRef<SVGSVGElement>(null);
    const isInView = useInView(ref, { margin: "100px" });

    return (
        <svg
            ref={ref}
            viewBox="0 0 600 600"
            className={`pointer-events-none absolute ${className}`}
            fill="none"
            aria-hidden
            data-testid="backdrop-contour"
        >
            <g className="animate-ridge" style={{ animationPlayState: isInView ? "running" : "paused" }}>
                {Array.from({ length: rings }).map((_, i) => (
                    <motion.circle
                        key={i}
                        cx="300"
                        cy="300"
                        r={24 + i * 19.5}
                        stroke={i % 4 === 3 ? "#A87B2F" : "#5B93E8"}
                        strokeWidth="1.1"
                        strokeLinecap="round"
                        strokeDasharray={`${150 + (i % 5) * 42} ${70 + (i % 3) * 34}`}
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 0.9 - i * 0.05 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2, delay: i * 0.06, ease: "easeOut" }}
                    />
                ))}
            </g>
        </svg>
    );
};

export const EvidenceTag = ({ label, className = "" }: { label: string; className?: string }) => (
    <span
        className={`inline-flex items-center gap-2 border border-white/20 bg-[#1A2B47] px-3 py-1.5 font-tech text-[9px] font-semibold uppercase tracking-[0.28em] text-[#E8ECF4] shadow-[2px_2px_0_#A87B2F] ${className}`}
        data-testid="evidence-tag"
    >
        <span className="h-1.5 w-1.5 rounded-full border border-white/30 bg-[#0F1B2E]" />
        {label}
    </span>
);

export const CornerMarks = ({ color = "#A87B2F" }: { color?: string }) => (
    <>
        <span className="pointer-events-none absolute left-0 top-0 h-5 w-5 border-l border-t" style={{ borderColor: color }} />
        <span className="pointer-events-none absolute right-0 top-0 h-5 w-5 border-r border-t" style={{ borderColor: color }} />
        <span className="pointer-events-none absolute bottom-0 left-0 h-5 w-5 border-b border-l" style={{ borderColor: color }} />
        <span className="pointer-events-none absolute bottom-0 right-0 h-5 w-5 border-b border-r" style={{ borderColor: color }} />
    </>
);
